import { PULSE_ITEM_TYPE, PULSE_ACTION_TYPE, PULSE_CATEGORY } from '@/modules/pulse/utils/pulseConstants';

export const pulseItemName = (item) => item?.context?.context_name || item?.actions?.[0]?.payload?.client_name || null;

export const pulseItemTitle = (item) => item?.display?.title || null;

export const pulseItemType = (item) => {
  const action = item?.actions?.[0]?.action;
  if (!action) return null;
  return action === PULSE_ACTION_TYPE.ACKNOWLEDGE
    ? PULSE_ITEM_TYPE.TAKE_A_LOOK
    : PULSE_ITEM_TYPE.ACTION_READY;
};

// Actions that prepare nothing to send: the user reviews the item (`acknowledge`)
// or is taken to its context (`navigate`). Both fall through to the open-the-page
// CTA rather than being offered a Send that has nothing to send.
const ARTIFACT_FREE_ACTIONS = new Set([
  PULSE_ACTION_TYPE.ACKNOWLEDGE,
  PULSE_ACTION_TYPE.NAVIGATE,
]);

// Whether the item carries something to send — which decides the primary CTA (send vs
// open the entity page) for both `pulseItemHoverActions` and `PulseFeedView.onCardAction`.
// An item with no action at all has nothing to send and no `actions[0].uid` to resolve, so
// it reads as "take a look" rather than being offered a Send that could only fail.
export const pulseItemHasArtifact = (item) => {
  const action = item?.actions?.[0];
  if (!action) return false;
  const explicit = action.payload?.artifact ?? item?.display?.artifact;
  if (typeof explicit === 'boolean') return explicit;
  return !ARTIFACT_FREE_ACTIONS.has(action.action || null);
};

const CONTEXT_ROUTE = {
  client: 'app.client',
  appointment: 'app.appointment.show',
  payment: 'app.payment.show',
  conversation: 'app.conversation.show',
  document: 'app.document.show',
  estimate: 'app.estimate.show',
};

// Contexts that live in POV rather than Angular: a named route plus the path
// param carrying the context uid. `handleNavigation` routes `name` to POV and
// `route` to Angular, so the two maps stay separate rather than overloading one.
const CONTEXT_POV_ROUTE = {
  ai_rec_conversation: {
    name: 'online-presence-web-ai-widget-history-conversation',
    param: 'conversation_uid',
  },
};

export const pulseItemNavigation = (item) => {
  const type = item?.context?.context_type;
  const uid = item?.context?.context_uid;
  if (!uid) return null;

  const povRoute = CONTEXT_POV_ROUTE[type];
  if (povRoute) return { name: povRoute.name, params: { [povRoute.param]: uid } };

  const route = CONTEXT_ROUTE[type];
  if (!route) return null;
  return { route, queryParams: { uid } };
};

// Leading term of the Conversita topic title. Takes a category, not an item — the caller
// picks the stored category or the display bucket, which differ. Null when unrecognised.
// See the recommendation README, "Topic title".
const CATEGORY_LABEL_KEY = {
  needs_attention: 'pulse.categories.needs_attention',
  opportunity: 'pulse.categories.opportunity',
  upcoming: 'pulse.categories.upcoming',
  fyi: 'pulse.categories.fyi',
};

export const pulseCategoryLabelKey = (category) => CATEGORY_LABEL_KEY[category] || null;

// README, "Recommendation deep link from email/SMS".
export const pulseItemCategoryTab = (item) => {
  const dueAt = new Date(item?.due_at ?? NaN).getTime();
  if (!Number.isNaN(dueAt) && dueAt > Date.now()) return PULSE_CATEGORY.UPCOMING;
  if (item?.category === PULSE_CATEGORY.FYI) return PULSE_CATEGORY.FYI;
  if (item?.category === PULSE_CATEGORY.OPPORTUNITY) return PULSE_CATEGORY.OPPORTUNITY;
  return PULSE_CATEGORY.NEEDS_ATTENTION;
};

// The primary CTA's text: `btn_text` and only that (see the README, "CTA label").
export const pulseItemCtaLabel = (item) => {
  const btnText = item?.actions?.[0]?.display?.btn_text;
  return typeof btnText === 'string' && btnText.trim() ? btnText.trim() : null;
};

export const pulseItemBodyMarkdown = (item) => {
  const body = item?.actions?.[0]?.display?.body_markdown;
  return typeof body === 'string' && body.trim() ? body.trim() : null;
};

// Tag pill data — see pulse/README.md, "Tag pill".
export const pulseItemTag = (item) => {
  const text = Array.isArray(item?.tags) && typeof item.tags[0] === 'string' ? item.tags[0] : null;
  if (!text) return null;
  const payload = item?.actions?.[0]?.payload;
  const color = payload?.tag_text_color_code;
  const backgroundColor = payload?.tag_background_color_code;
  if (!color || !backgroundColor) return null;
  return { text, color, backgroundColor };
};

const MIRRORED_ICON = {
  'icon-Chevron-right': 'icon-Chevron-left',
  'icon-Chevron-left': 'icon-Chevron-right',
};

export const resolveDirectionalIcon = (icon, isRtl) => (isRtl && MIRRORED_ICON[icon]) || icon;

const DISMISS = {
  key: 'not_relevant', event: 'dismiss', icon: 'icon-Trash1', labelKey: 'pulse.item.not_relevant',
};
const MARK_DONE = {
  key: 'mark_done', event: 'complete', icon: 'icon-check', iconSize: '26', labelKey: 'pulse.item.mark_done',
};
const VIEW = {
  key: 'open_in_chat', event: 'open-chat', icon: 'icon-Chevron-right', labelKey: 'pulse.item.open_in_chat',
};
const SEND = {
  key: 'send',
  event: 'primary',
  labelFromCta: true,
  labelKey: 'pulse.item.action_cta.fallback',
};
// `navigate` items have no artifact to send; their primary CTA takes the user to
// the item's context, labeled from the recommendation's own CTA text rather than
// VIEW's generic "open in chat" wording.
const NAVIGATE = {
  key: 'navigate',
  event: 'primary',
  labelFromCta: true,
  labelKey: 'pulse.item.action_cta.fallback',
};

// Returns { send, actions, primary } — see pulse/README.md, "The hover row".
// `send` renders as its own primary CTA, separate from the ghost dismiss actions.
export const pulseItemHoverActions = (item) => {
  // Without this, a `navigate` item falls into the default case below, whose
  // primary (VIEW) ignores the CTA label — the navigation branch of
  // `onCardAction` could never be reached with a meaningful button label.
  if (item?.actions?.[0]?.action === PULSE_ACTION_TYPE.NAVIGATE) {
    return { send: null, actions: [DISMISS], primary: NAVIGATE };
  }

  return {
    send: pulseItemHasArtifact(item) ? SEND : null,
    actions: [MARK_DONE, DISMISS],
    primary: VIEW,
  };
};

// What the mobile sheet shows in place of the desktop row's chevron slot and bare Send:
// its own glyph and wording. Send carries no glyph at all. Spread over a copy, never onto
// the shared constants above — desktop reads the same objects.
const SHEET_OVERRIDE = {
  open_in_chat: { icon: 'icon-Pulse-menu', labelKey: 'pulse.item.view_details' },
  send: { labelKey: 'pulse.item.send_now' },
};

// The mobile tile's kebab sheet (VCITA2-15779) — the same actions the desktop hover row
// offers, flattened into one bottom-anchored list. Send closes the list. Desktop's shape
// is untouched; this is a second reading of the same source.
export const pulseItemSheetActions = (item) => {
  const { send, actions, primary } = pulseItemHoverActions(item);
  return [primary, ...actions, send]
    .filter(Boolean)
    .map((action) => ({ ...action, ...SHEET_OVERRIDE[action.key] }));
};
